/**
 * ARCHITECTURE: the screen locks after 5 minutes without activity
 * (configurable). One timer, armed for the time that is left, not re-armed on
 * every key press; a device that slept past the limit locks as soon as it
 * wakes (`check` on visibility).
 */

export interface Timers {
  now(): number
  setTimeout(callback: () => void, milliseconds: number): unknown
  clearTimeout(handle: unknown): void
}

const browserTimers: Timers = {
  now: () => Date.now(),
  setTimeout: (callback, milliseconds) => globalThis.setTimeout(callback, milliseconds),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
}

export interface IdleWatch {
  /** Someone touched the screen or a key. */
  activity(): void
  /** Look again now, for example when the page becomes visible. */
  check(): void
  stop(): void
}

export function watchIdle(
  timeoutMs: number,
  onIdle: () => void,
  timers: Timers = browserTimers,
): IdleWatch {
  let last = timers.now()
  let handle: unknown = null
  let stopped = false

  const arm = (milliseconds: number) => {
    if (handle !== null) timers.clearTimeout(handle)
    handle = timers.setTimeout(check, milliseconds)
  }

  function check() {
    if (stopped) return
    const left = last + timeoutMs - timers.now()
    if (left > 0) {
      arm(left)
      return
    }
    stop()
    onIdle()
  }

  function stop() {
    stopped = true
    if (handle !== null) timers.clearTimeout(handle)
    handle = null
  }

  arm(timeoutMs)
  return {
    activity: () => {
      if (!stopped) last = timers.now()
    },
    check,
    stop,
  }
}

/**
 * C2: what the web app does with a session it finds when it starts, going by
 * the last time someone used it before it closed or reloaded. Within the limit
 * it resumes. Past it, the session was left alone (`idle`). With no time to go
 * by, or one later than now (a clock set back), there is no telling
 * (`unknown`). Both of those end it and ask for the PIN; only `idle` can say
 * the screen was left alone.
 */
export function sessionAtStart(
  lastActivity: number | null,
  now: number,
  timeoutMs: number,
): 'resume' | 'idle' | 'unknown' {
  if (lastActivity === null || lastActivity > now) return 'unknown'
  return now - lastActivity >= timeoutMs ? 'idle' : 'resume'
}

/** A recorded activity time read back: whole milliseconds since 1970, or nothing. */
export function readActivity(stored: string | null): number | null {
  if (stored === null || !/^\d{1,15}$/.test(stored)) return null
  return Number(stored)
}

/** The idle limit from the build's settings: whole minutes, 1 to 60, 5 when unset or nonsense. */
export function idleMinutes(setting: string | undefined): number {
  const minutes = Number(setting)
  return Number.isInteger(minutes) && minutes >= 1 && minutes <= 60 ? minutes : 5
}
